import { FC } from 'react';
import styled from '@emotion/styled';
import { ExternalLink } from 'components/core/link/Link';
import { Text } from 'components/core/text';
import { color } from 'components/core/theme';
import { mediaMobile } from 'components/core/theme/breakpoints';

export interface FooterProps {
  leftHelpTitle?: string;
  leftHelpTextLink?: string;
  leftHelpTextTitle?: string;
  leftHelpCallLink?: string;
  leftHelpCallText?: string;
  leftHelpUrlLink?: string;
  leftHelpUrlText?: string;
  rightHelpTitle?: string;
  leftEmailLink?: string;
  rightEmailLink?: string;
  rightPrivacyLink?: string;
  rightTermsLink?: string;
  rightCompanyText?: string;
  centerTitleText?: string;
}

export const Footer = styled.div`
  width: 100%;
  position: relative;
  bottom: 0;
  top: auto;
  left: 0;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(59.8px);
  padding: 1em;
  margin-top: 1.5rem;
`;

const FooterContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font-size: 0.75rem;
  font-weight: normal;

  ${mediaMobile} {
    flex-direction: column;
    align-items: start;
    gap: 24px;
    padding-left: 4px;
  }
`;

const NormalBold = styled.div`
  font-weight: normal;
`;

const RightContent = styled.div`
  text-align: right;
  font-weight: bold;

  ${mediaMobile} {
    text-align: left;
  }
`;

const LeftContent = styled.div`
  text-align: left;
  font-weight: bold;
`;

const CenterContent = styled.div`
  color: ${color('tealDark')};
  text-align: center;
  margin-top: 10px;
  font-size: 0.75rem;
  border-radius: 8px;
  line-height: 150%;
  padding-top: 12px;
  padding-bottom: 12px;

  background: linear-gradient(
    89.99deg,
    rgba(8, 156, 156, 0.2) 0%,
    rgba(57, 188, 102, 0.2) 99.99%
  );
`;

const SpaceSubject = styled.div`
  margin-top: 5px;
  font-size: 0.75rem;
`;

const FooterBar: FC<FooterProps> = () => {
  const footerData: FooterProps = {
    leftHelpTitle: 'Emergency help resources',
    leftHelpTextLink: 'Text 741741 to reach the crisis text line',
    leftHelpCallLink: 'Call 988 to reach the mental health crisis hotline',
    leftHelpUrlLink: 'findhelp.org ',
    leftHelpUrlText: 'for local resources',
    rightHelpTitle: 'Need Help? ',
    leftEmailLink: 'Contact us at ',
    rightEmailLink: ' support@bendhealth.com',
    rightPrivacyLink: 'Privacy Practices',
    rightTermsLink: ' Terms, Conditions & Consent',
    rightCompanyText: '© Bend Health Inc.  All Rights Reserved',
    centerTitleText:
      'Bend Health clinical services are provided by licensed practitioners.',
  };

  return (
    <Footer>
      <FooterContent>
        <LeftContent>
          {footerData.leftHelpTitle ? (
            <div>
              <Text variant="text6" bold color="redDark">
                {footerData.leftHelpTitle}
              </Text>
            </div>
          ) : null}
          {footerData.leftHelpTextLink ? (
            <SpaceSubject>
              <LeftContent>{footerData.leftHelpTextLink}</LeftContent>
            </SpaceSubject>
          ) : null}
          {footerData.leftHelpCallLink ? (
            <SpaceSubject>
              <LeftContent>{footerData.leftHelpCallLink}</LeftContent>
            </SpaceSubject>
          ) : null}
          {footerData.leftHelpUrlLink ? (
            <SpaceSubject>
              <LeftContent>
                <NormalBold>
                  Visit{' '}
                  <ExternalLink
                    href={'http://findhelp.org/'}
                    variant="text6"
                    bold
                  >
                    {footerData.leftHelpUrlLink}
                  </ExternalLink>
                  for local resources
                </NormalBold>
              </LeftContent>
            </SpaceSubject>
          ) : null}
        </LeftContent>
        <RightContent>
          {footerData.rightHelpTitle ? (
            <div>
              <Text variant="text6" bold color="tealDark">
                {footerData.rightHelpTitle}
              </Text>
            </div>
          ) : null}
          {footerData.rightEmailLink ? (
            <div>
              {footerData.leftEmailLink}
              <ExternalLink
                href={'mailto:support@bendhealth.com'}
                variant="text6"
                bold
              >
                {footerData.rightEmailLink}
              </ExternalLink>
            </div>
          ) : null}
          {footerData.rightPrivacyLink ? (
            <SpaceSubject>
              <ExternalLink
                variant="text6"
                bold
                href={'https://www.bendhealth.com/website-privacy-policy'}
              >
                {footerData.rightPrivacyLink}
              </ExternalLink>
              <ExternalLink
                variant="text6"
                bold
                href={'https://www.bendhealth.com/website-terms-of-service'}
              >
                {footerData.rightTermsLink}
              </ExternalLink>
            </SpaceSubject>
          ) : null}
          {footerData.rightCompanyText ? (
            <SpaceSubject>
              <NormalBold>{footerData.rightCompanyText}</NormalBold>
            </SpaceSubject>
          ) : null}
        </RightContent>
      </FooterContent>
      {footerData.centerTitleText ? (
        <div className="py-2">
          <CenterContent>{footerData.centerTitleText}</CenterContent>
        </div>
      ) : null}
    </Footer>
  );
};

export default FooterBar;
